import { useEffect } from 'react';

export interface MediaSessionOptions {
  title: string;
  artist: string;
  album?: string;
  artwork?: string;
  onPlay?: () => void;
  onPause?: () => void;
  onSeekForward?: () => void;
  onSeekBackward?: () => void;
  onSeekTo?: (time: number) => void;
  onPrevTrack?: () => void;
  onNextTrack?: () => void;
}

/**
 * Hook to integrate with the Media Session API for lockscreen/notification controls
 */
export function useMediaSession(options: MediaSessionOptions): void {
  const {
    title,
    artist,
    album,
    artwork,
    onPlay,
    onPause,
    onSeekForward,
    onSeekBackward,
    onSeekTo,
    onPrevTrack,
    onNextTrack,
  } = options;

  useEffect(() => {
    if (!('mediaSession' in navigator)) {
      return;
    }

    // Set metadata
    const artworkArray = artwork
      ? [
          { src: artwork, sizes: '96x96', type: 'image/png' },
          { src: artwork, sizes: '128x128', type: 'image/png' },
          { src: artwork, sizes: '192x192', type: 'image/png' },
          { src: artwork, sizes: '256x256', type: 'image/png' },
          { src: artwork, sizes: '384x384', type: 'image/png' },
          { src: artwork, sizes: '512x512', type: 'image/png' },
        ]
      : [];

    navigator.mediaSession.metadata = new MediaMetadata({
      title,
      artist,
      album: album || '',
      artwork: artworkArray,
    });
  }, [title, artist, album, artwork]);

  // Set action handlers
  useEffect(() => {
    if (!('mediaSession' in navigator)) {
      return;
    }

    const handlers: [MediaSessionAction, MediaSessionActionHandler | null][] = [
      ['play', onPlay ? () => onPlay() : null],
      ['pause', onPause ? () => onPause() : null],
      ['seekforward', onSeekForward ? () => onSeekForward() : null],
      ['seekbackward', onSeekBackward ? () => onSeekBackward() : null],
      ['seekto', onSeekTo ? (details) => details.seekTime !== undefined && onSeekTo(details.seekTime) : null],
      ['previoustrack', onPrevTrack ? () => onPrevTrack() : null],
      ['nexttrack', onNextTrack ? () => onNextTrack() : null],
    ];

    for (const [action, handler] of handlers) {
      try {
        navigator.mediaSession.setActionHandler(action, handler);
      } catch {
        // Some actions may not be supported
      }
    }

    return () => {
      for (const [action] of handlers) {
        try {
          navigator.mediaSession.setActionHandler(action, null);
        } catch {
          // Ignore cleanup errors
        }
      }
    };
  }, [onPlay, onPause, onSeekForward, onSeekBackward, onSeekTo, onPrevTrack, onNextTrack]);
}

/**
 * Update the Media Session position state
 */
export function updateMediaSessionPosition(
  currentTime: number,
  duration: number,
  playbackRate: number
): void {
  if (!('mediaSession' in navigator) || !navigator.mediaSession.setPositionState) {
    return;
  }

  try {
    if (duration > 0 && isFinite(duration)) {
      navigator.mediaSession.setPositionState({
        duration,
        playbackRate,
        position: currentTime,
      });
    }
  } catch {
    // Ignore errors from invalid position state
  }
}
